/**
 * Shrink-to-fit font sizing via Canvas text measurement.
 *
 * Mirrors the iOS `adjustsFontSizeToFitWidth` + `minimumScaleFactor` behaviour: text
 * starts at its nominal size and only scales DOWN to fit the available width (across an
 * optional line budget), never below a floor.
 *
 * Same technique the Pass Builder preview uses (`bannerValueSize` in
 * AppleWalletPassPreview) — measure once at a reference size with Canvas, then derive the
 * fitting size by ratio. Pure calculation: no DOM refs, no reflow, safe to call in render
 * (memoize on the inputs). The Pass Builder's banner variant can be expressed as
 * `fitFontSize({ ..., minScale: <floor>, growToFill: true })` when the branches converge.
 */

/** System UI font stack (≈ iOS SF Pro). Render text in this so wrapping matches the measurement. */
export const SYSTEM_FONT = "system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"
const REF_SIZE = 100
// Text measured to exactly fill its box still wraps at the boundary (line-breaking is
// word-granular and the measured advance width is a hair under the laid-out width). Fit to
// a slightly narrower width so one-line text reliably stays on one line. ~3% measured.
const FIT_SAFETY = 0.96

export type FitFontSizeOptions = {
  text: string
  /** Nominal (maximum) font size in px. */
  maxFontPx: number
  /** Width the text must fit within, in px. */
  availableWidthPx: number
  /** Lower bound as a fraction of `maxFontPx` (e.g. 0.6 = down to 60%). Default 1 (no shrink). */
  minScale?: number
  /**
   * When keeping the text on fewer lines than the budget (preferSingleLine), how much it
   * may shrink before wrapping to the next line, as a fraction of `maxFontPx`. Higher =
   * wrap sooner (tolerate less shrink on one line). Defaults to `minScale` (shrink all the
   * way to the floor before wrapping). Only used when `preferSingleLine` is true.
   */
  fewerLinesMinScale?: number
  /** Number of lines the text may wrap across before it must shrink. Default 1. */
  maxLines?: number
  /**
   * How to spend the line budget:
   * - `true` (default): keep the text on as few lines as possible, shrinking toward the
   *   floor before wrapping (good for titles that read best on one line).
   * - `false`: use the full line budget at nominal size first and only shrink if the text
   *   still overflows (good for narrow buttons where labels naturally stack).
   */
  preferSingleLine?: boolean
  fontWeight?: number | string
  fontFamily?: string
}

export type FitResult = {
  /** Fitted font size in px. */
  fontPx: number
  /** Number of lines the text occupies at `fontPx` (1..maxLines). */
  lines: number
}

/** Fitted font size plus the number of lines it occupies, so callers can reserve height. */
export const fitText = ({
  text,
  maxFontPx,
  availableWidthPx,
  minScale = 1,
  fewerLinesMinScale,
  maxLines = 1,
  preferSingleLine = true,
  fontWeight = 400,
  fontFamily = SYSTEM_FONT,
}: FitFontSizeOptions): FitResult => {
  const minFontPx = maxFontPx * minScale
  if (!text || availableWidthPx <= 0 || maxFontPx <= 0) {
    return { fontPx: maxFontPx, lines: 1 }
  }

  // Two width constraints: the whole string fitting one line, and the widest single word.
  // A word can't be broken, so no matter how many lines we use, the size can never exceed
  // the point where the longest word fits one line — otherwise it overflows/truncates.
  const { singleLineFontPx, wordLineFontPx } = measureFitFontPx(
    text,
    availableWidthPx,
    fontWeight,
    fontFamily,
  )
  const lineBudget = Math.max(maxLines, 1)
  // Size that fits `lines` lines: even-split estimate, capped by the unbreakable word.
  const lineFitPx = (lines: number) => Math.min(singleLineFontPx * lines, wordLineFontPx)
  const fewerLinesFloor = maxFontPx * (fewerLinesMinScale ?? minScale)

  const fontPx = (() => {
    if (!preferSingleLine) {
      // Spend the whole line budget at nominal size first; shrink only if it still overflows.
      return Math.min(maxFontPx, Math.max(minFontPx, lineFitPx(lineBudget)))
    }
    // Prefer fewer lines: keep the text on a given line count only while it stays above the
    // "fewer lines" threshold; below that, wrap to the next line rather than shrink further.
    for (let lines = 1; lines < lineBudget; lines += 1) {
      const candidate = lineFitPx(lines)
      if (candidate >= fewerLinesFloor) {
        return Math.min(maxFontPx, candidate)
      }
    }

    // Largest allowed line count: shrink all the way to the absolute floor if needed.
    return Math.min(maxFontPx, Math.max(minFontPx, lineFitPx(lineBudget)))
  })()

  // Width scales linearly with font size, so the chosen size relative to the one-line fit
  // tells us how many lines the text wraps onto.
  const lines = Math.min(lineBudget, Math.max(1, Math.ceil(fontPx / singleLineFontPx - 1e-3)))

  return { fontPx, lines }
}

/** Returns the font size (px) at which `text` fits `availableWidthPx`, clamped to the floor. */
const fitFontSize = (options: FitFontSizeOptions): number => fitText(options).fontPx

const APPROX_CHAR_EM = 0.55

type FitWidths = {
  /** Font size (px) at which the whole string fits one line. */
  singleLineFontPx: number
  /** Font size (px) at which the widest single (unbreakable) word fits one line. */
  wordLineFontPx: number
}

/** Font sizes at which `text`, and its widest word, each fit `availableWidthPx` on one line. */
const measureFitFontPx = (
  text: string,
  availableWidthPx: number,
  fontWeight: number | string,
  fontFamily: string,
): FitWidths => {
  const words = text.split(/\s+/).filter(Boolean)
  const safeWidth = availableWidthPx * FIT_SAFETY
  try {
    const ctx = document.createElement("canvas").getContext("2d")
    if (!ctx) throw new Error("no 2d context")
    ctx.font = `${fontWeight} ${REF_SIZE}px ${fontFamily}`
    const fullWidth = ctx.measureText(text).width
    if (!fullWidth) throw new Error("empty measurement")
    const maxWordWidth = words.reduce((max, word) => Math.max(max, ctx.measureText(word).width), 0)

    return {
      singleLineFontPx: (safeWidth * REF_SIZE) / fullWidth,
      wordLineFontPx: (safeWidth * REF_SIZE) / (maxWordWidth || fullWidth),
    }
  } catch {
    // Canvas unavailable (e.g. SSR/jsdom): rough char-width estimate (~0.55em/char),
    // same spirit as the Pass Builder fallback.
    const longestWordLen = words.reduce((max, word) => Math.max(max, word.length), 0)

    return {
      singleLineFontPx: safeWidth / Math.max(text.length * APPROX_CHAR_EM, 1),
      wordLineFontPx: safeWidth / Math.max(longestWordLen * APPROX_CHAR_EM, 1),
    }
  }
}

export default fitFontSize
